/**
 * Waste Log — mobile-first discard logging. Full reporting lives in Inventory Insights.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { WASTE_REASONS, type WasteReason, wasteReasonLabel } from '@cafe-pos/shared';
import type { Database } from '@cafe-pos/supabase';
import { fmtDt, parseWasteNote } from '../inventoryPeriod.js';
import { supabase } from '../supabase.js';
import { BarChart3, ClipboardPen, RotateCcw } from 'lucide-react';
import {
  Button,
  Callout,
  Card,
  CardBody,
  CardHeader,
  ConfirmDialog,
  EmptyState,
  Field,
  FormGrid,
  PageHeader,
} from '../ui/index.js';

type IngredientRow = Database['public']['Tables']['ingredients']['Row'];
type InvTxRow = Database['public']['Tables']['inventory_transactions']['Row'];
type RecipientRow = Pick<Database['public']['Tables']['staff']['Row'], 'id' | 'name'>;

type WasteRow = InvTxRow & { ingredient_name: string; unit: string };

export function WasteLogView({
  clientId,
  onNavigate,
}: {
  clientId: string | null;
  onNavigate?: (view: string) => void;
}) {
  const { t } = useTranslation();
  const [ingredients, setIngredients] = useState<IngredientRow[]>([]);
  const [recipients, setRecipients] = useState<RecipientRow[]>([]);
  const [recent, setRecent] = useState<WasteRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [confirmCancel, setConfirmCancel] = useState<WasteRow | null>(null);

  const [ingredientId, setIngredientId] = useState('');
  const [ingredientSearch, setIngredientSearch] = useState('');
  const [quantity, setQuantity] = useState('');
  const [reason, setReason] = useState<WasteReason>('expired');
  const [recipientId, setRecipientId] = useState('');
  const [noteExtra, setNoteExtra] = useState('');

  async function load() {
    if (!supabase || !clientId) return;
    setLoading(true);
    setError(null);

    const [ingRes, txRes, recRes] = await Promise.all([
      supabase.from('ingredients').select('*').eq('client_id', clientId).order('name'),
      supabase
        .from('inventory_transactions')
        .select('*')
        .eq('client_id', clientId)
        .eq('type', 'waste')
        .order('created_at', { ascending: false })
        .limit(8),
      supabase
        .from('staff')
        .select('id, name')
        .eq('client_id', clientId)
        .eq('active', true)
        .order('name'),
    ]);

    if (ingRes.error) {
      setError(ingRes.error.message);
      setLoading(false);
      return;
    }
    if (txRes.error) {
      setError(txRes.error.message);
      setLoading(false);
      return;
    }
    if (recRes.error) {
      setError(recRes.error.message);
      setLoading(false);
      return;
    }

    setIngredients(ingRes.data ?? []);
    setRecipients((recRes.data ?? []) as RecipientRow[]);
    const ingById = Object.fromEntries((ingRes.data ?? []).map((i) => [i.id, i]));
    setRecent(
      (txRes.data ?? []).map((tx) => ({
        ...tx,
        ingredient_name: ingById[tx.ingredient_id]?.name ?? t('common.unknown'),
        unit: ingById[tx.ingredient_id]?.unit ?? '',
      })),
    );
    setLoading(false);
  }

  useEffect(() => {
    void load();
  }, [clientId]);

  async function logWaste() {
    if (!supabase || !clientId) return;

    const qty = Number(quantity);
    if (!ingredientId) {
      setError(t('wasteLog.pickIngredient'));
      return;
    }
    if (!qty || qty <= 0) {
      setError(t('wasteLog.positiveQty'));
      return;
    }

    const ing = ingredients.find((i) => i.id === ingredientId);
    if (!ing) return;

    if (reason === 'other' && !noteExtra.trim()) {
      setError(t('wasteLog.commentRequired'));
      return;
    }
    if (reason === 'staff_meal' && !recipientId) {
      setError(t('wasteLog.recipientRequired'));
      return;
    }

    setSaving(true);
    setError(null);

    const newStock = Math.max(0, Number(ing.stock) - qty);
    const { error: updateErr } = await supabase
      .from('ingredients')
      .update({ stock: newStock })
      .eq('id', ingredientId);

    if (updateErr) {
      setError(updateErr.message);
      setSaving(false);
      return;
    }

    const { data: userData } = await supabase.auth.getUser();
    const recipientName = recipients.find((r) => r.id === recipientId)?.name;
    const detail =
      reason === 'staff_meal'
        ? [recipientName, noteExtra.trim()].filter(Boolean).join(' — ')
        : noteExtra.trim();
    const noteText = detail ? `${reason} — ${detail}` : reason;

    await supabase.from('inventory_transactions').insert({
      client_id: clientId,
      ingredient_id: ingredientId,
      delta: -qty,
      type: 'waste' as const,
      note: noteText,
      created_by: userData?.user?.id ?? null,
    });

    setIngredientId('');
    setIngredientSearch('');
    setQuantity('');
    setReason('expired');
    setRecipientId('');
    setNoteExtra('');
    setSaving(false);
    void load();
  }

  async function cancelWaste(row: WasteRow) {
    if (!supabase || row.reverted_at) return;
    setCancellingId(row.id);
    setError(null);
    const { error: err } = await supabase.rpc('revert_waste_transaction', { p_tx_id: row.id });
    setCancellingId(null);
    if (err) {
      setError(err.message);
      return;
    }
    setConfirmCancel(null);
    void load();
  }

  function openInsights() {
    window.history.pushState(null, '', '#/inventory-insights?tab=loss');
    onNavigate?.('inventory-insights');
  }

  const pickerIngredients = ingredients.filter((i) => i.active !== false);
  const selectedIngredient = pickerIngredients.find((i) => i.id === ingredientId);

  return (
    <section className="waste-log-page ui-stack">
      <PageHeader
        title={t('wasteLog.title')}
        subtitle={t('wasteLog.subtitle')}
        actions={
          onNavigate ? (
            <Button variant="secondary" icon={<BarChart3 />} onClick={openInsights}>
              {t('wasteLog.viewInsights')}
            </Button>
          ) : undefined
        }
      />

      <datalist id="waste-ingredients">
        {pickerIngredients.map((i) => (
          <option key={i.id} value={i.name} />
        ))}
      </datalist>

      {error && <Callout tone="danger">{error}</Callout>}

      <Card>
        <CardHeader title={t('wasteLog.newEntry')} caption={t('wasteLog.newCaption')} />
        <CardBody>
          <div className="waste-form">
            <Field label={t('wasteLog.ingredient')} htmlFor="waste-ingredient" hint={selectedIngredient ? `${t('wasteLog.stock')}: ${selectedIngredient.stock} ${selectedIngredient.unit}` : undefined}>
              <input
                id="waste-ingredient"
                list="waste-ingredients"
                placeholder={t('wasteLog.ingredientPlaceholder')}
                value={ingredientSearch}
                onChange={(e) => {
                  const val = e.target.value;
                  setIngredientSearch(val);
                  const match = pickerIngredients.find((i) => i.name.toLowerCase() === val.toLowerCase());
                  setIngredientId(match?.id ?? '');
                }}
                onBlur={(e) => {
                  const match = pickerIngredients.find(
                    (i) => i.name.toLowerCase() === e.target.value.toLowerCase(),
                  );
                  if (!match) {
                    setIngredientSearch('');
                    setIngredientId('');
                  }
                }}
                autoComplete="off"
              />
            </Field>

            <FormGrid>
              <Field label={t('wasteLog.qty')} htmlFor="waste-qty">
                <div className="waste-qty">
                  <input
                    id="waste-qty"
                    type="number"
                    min="0"
                    step="any"
                    inputMode="decimal"
                    placeholder="0"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                  />
                  {selectedIngredient && <span className="waste-qty-unit">{selectedIngredient.unit}</span>}
                </div>
              </Field>
              <Field label={t('wasteLog.reason')} htmlFor="waste-reason">
                <select
                  id="waste-reason"
                  value={reason}
                  onChange={(e) => {
                    const next = e.target.value as WasteReason;
                    setReason(next);
                    if (next !== 'staff_meal') setRecipientId('');
                  }}
                >
                  {WASTE_REASONS.map((r) => (
                    <option key={r} value={r}>
                      {t(`wasteReasons.${r}`)}
                    </option>
                  ))}
                </select>
              </Field>
            </FormGrid>

            {reason === 'staff_meal' && (
              <Field label={t('wasteLog.compRecipient')} htmlFor="waste-recipient">
                <select
                  id="waste-recipient"
                  value={recipientId}
                  onChange={(e) => setRecipientId(e.target.value)}
                >
                  <option value="">{t('wasteLog.selectRecipient')}</option>
                  {recipients.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </select>
              </Field>
            )}

            <Field
              label={t('wasteLog.note')}
              htmlFor="waste-note"
              optional={reason !== 'other'}
            >
              <input
                id="waste-note"
                placeholder={reason === 'other' ? t('wasteLog.commentPlaceholder') : t('wasteLog.notePlaceholder')}
                value={noteExtra}
                onChange={(e) => setNoteExtra(e.target.value)}
              />
            </Field>

            <div className="waste-form-actions">
              <Button
                variant="primary"
                icon={<ClipboardPen />}
                onClick={() => void logWaste()}
                disabled={
                  saving ||
                  !ingredientId ||
                  !quantity ||
                  (reason === 'other' && !noteExtra.trim()) ||
                  (reason === 'staff_meal' && !recipientId)
                }
              >
                {saving ? t('common.loading') : t('wasteLog.submit')}
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>

      <Card layout="flush">
        <CardHeader
          title={t('wasteLog.recent')}
          right={
            onNavigate && recent.length > 0 ? (
              <Button variant="text" size="sm" onClick={openInsights}>
                {t('wasteLog.viewAll')} →
              </Button>
            ) : undefined
          }
        />
        {loading ? (
          <div className="waste-log-empty muted">{t('common.loading')}</div>
        ) : recent.length === 0 ? (
          <EmptyState compact title={t('wasteLog.none')} />
        ) : (
          <ul className="waste-log-list">
            {recent.map((row) => {
              const parsed = parseWasteNote(row.note);
              const cancelled = Boolean(row.reverted_at);
              return (
                <li key={row.id} className="waste-log-item">
                  <div className="waste-log-item-top">
                    <strong style={cancelled ? { textDecoration: 'line-through' } : undefined}>
                      {row.ingredient_name}
                    </strong>
                    <span className="waste-log-item-qty">
                      −{Math.abs(row.delta)} {row.unit}
                    </span>
                  </div>
                  <div className="muted waste-log-item-meta">
                    {fmtDt(row.created_at)} · {t(`wasteReasons.${parsed.reason}`, { defaultValue: wasteReasonLabel(parsed.reason) })}
                    {parsed.detail ? ` · ${parsed.detail}` : ''}
                    {cancelled ? ` · ${t('wasteLog.cancelled')}` : ''}
                  </div>
                  {!cancelled && (
                    <div className="waste-log-item-actions">
                      <Button
                        variant="text"
                        size="sm"
                        icon={<RotateCcw />}
                        disabled={cancellingId === row.id}
                        onClick={() => setConfirmCancel(row)}
                      >
                        {cancellingId === row.id ? t('wasteLog.cancelling') : t('wasteLog.cancelEntry')}
                      </Button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      <ConfirmDialog
        open={confirmCancel != null}
        onClose={() => {
          if (!cancellingId) setConfirmCancel(null);
        }}
        onConfirm={() => {
          if (confirmCancel) void cancelWaste(confirmCancel);
        }}
        title={t('wasteLog.cancelTitle')}
        message={
          confirmCancel
            ? t('wasteLog.cancelMessage', {
                qty: Math.abs(confirmCancel.delta),
                unit: confirmCancel.unit,
                name: confirmCancel.ingredient_name,
              })
            : undefined
        }
        confirmLabel={t('wasteLog.cancelEntry')}
        busy={cancellingId === confirmCancel?.id}
      />
    </section>
  );
}
